import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ApplicantForm from "@/components/ApplicantForm";
import AddOnDetailsForm from "@/components/AddOnDetailsForm";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { emptyApplicant, applicantIsComplete, applicantDisplayName, stepIsComplete, STEPS } from "@/lib/applicants";
import { worldCountryByCode } from "@/lib/worldCountries";
import { emptyAddOnDetails, addOnsAreComplete } from "@/lib/addons";
import { CheckCircle2, Circle, ArrowRight, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ADDON_STEP = { key: "addons", label: "Add-on Details" };

// Short labels for the breadcrumb on custom orders. Keys mirror buildDocs IDs / addon IDs.
const DOC_LABELS = {
  flight: "Flight Reservation",
  hotel: "Hotel Booking",
  visa: "Visa Application Form",
  car: "Car Rental",
  cover: "Cover Letter",
  itinerary: "Travel Itinerary",
  insurance: "Travel Insurance",
};

export default function ApplyApplicants() {
  const { orderId, idx: idxParam } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [addOnDetails, setAddOnDetails] = useState(emptyAddOnDetails());
  const [activeIdx, setActiveIdx] = useState(parseInt(idxParam || "0", 10));
  const [saving, setSaving] = useState(false);
  const [stepKey, setStepKey] = useState("passport");
  // Scroll to top on every step / applicant change AND on initial wizard mount.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [stepKey, activeIdx]);
  // Center the active step tab inside the tab strip ONLY when the step changes.
  // Scrolls the strip container horizontally — never the page.
  const tabsRef = useRef(null);
  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-testid="step-tab-${stepKey}"]`);
    if (!el) return;
    const left = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
    container.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [stepKey]);
  const [savedAt, setSavedAt] = useState(null); // Date | null
  const [saveError, setSaveError] = useState(false);
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setNowTick((t) => t + 1), 15000); return () => clearInterval(id); }, []);

  const saveStatus = saving ? "saving" : saveError ? "error" : savedAt ? "saved" : "idle";
  const relativeSavedAgo = (() => {
    if (!savedAt) return "";
    const secs = Math.floor((Date.now() - savedAt.getTime()) / 1000);
    if (secs < 5) return "Saved";
    if (secs < 60) return `Saved ${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Saved ${mins}m ago`;
    return `Saved ${Math.floor(mins / 60)}h ago`;
  })();
  // Tick state below forces re-render every 15s so the "Saved Xs ago" label refreshes.
  void nowTick;

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?redirect=/apply/applicants/${orderId}`); return; }
    api.get(`/orders/${orderId}`).then(({ data }) => {
      setOrder(data);
      const existing = Array.isArray(data.applicants) ? data.applicants : [];
      const n = data.num_applicants || 1;
      const filled = Array.from({ length: n }, (_, i) => existing[i] || emptyApplicant());
      // Seed applicant 1 with order-level info if blank
      if (!filled[0].first_names && !filled[0].surname && data.full_name) {
        const parts = (data.full_name || "").trim().split(/\s+/);
        filled[0].first_names = parts.slice(0, -1).join(" ") || parts[0] || "";
        filled[0].surname = parts.length > 1 ? parts[parts.length - 1] : "";
      }
      if (!filled[0].email && data.email) filled[0].email = data.email;
      if (!filled[0].phone && data.whatsapp) filled[0].phone = data.whatsapp;
      if (!filled[0].relationship && n > 1) filled[0].relationship = "Self";
      // Pre-fill "Country of residence" (Step 2) from the order's applying_from_country
      // for every applicant that doesn't already have one. User can still edit.
      const applyingFromName = worldCountryByCode(data.applying_from_country)?.name || "";
      if (applyingFromName) {
        for (const a of filled) {
          if (!a.applicant_country) a.applicant_country = applyingFromName;
          if (!a.residence_country) a.residence_country = applyingFromName;
        }
      }
      setApplicants(filled);
      // Merge any saved add-on details, then pre-fill NOC employer fields from applicant 0
      const savedDetails = { ...emptyAddOnDetails(), ...(data.add_on_details || {}) };
      if ((data.add_ons || []).includes("noc")) {
        const a0 = filled[0] || {};
        const noc = savedDetails.noc || {};
        savedDetails.noc = {
          ...noc,
          employer_name: noc.employer_name || a0.employer_name || "",
          employer_address: noc.employer_address || a0.employer_address || "",
          employer_phone: noc.employer_phone || a0.employer_phone || "",
        };
      }
      setAddOnDetails(savedDetails);
    }).catch(() => {
      toast.error("Order not found");
      navigate("/dashboard");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, authLoading, user]);

  useEffect(() => {
    const i = parseInt(idxParam || "0", 10);
    if (!Number.isNaN(i)) setActiveIdx(i);
  }, [idxParam]);

  // Reset to first step when switching applicants
  useEffect(() => { setStepKey("passport"); }, [activeIdx]);

  // If URL has ?step=addons, jump into that step (when allowed)
  useEffect(() => {
    if (!order) return;
    const qs = new URLSearchParams(window.location.search).get("step");
    const n = order.num_applicants || 1;
    const onLast = activeIdx === n - 1;
    const orderHasAddOns = (order.add_ons || []).length > 0;
    if (qs === "addons" && onLast && orderHasAddOns) setStepKey("addons");
  }, [order, activeIdx]);

  if (authLoading || !order) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f8fafc]">
        <Header />
        <main className="flex-1 grid place-items-center text-[#475569]">Loading…</main>
        <Footer />
      </div>
    );
  }

  const n = applicants.length || 1;
  const showSidebar = n > 1;
  const showRelationship = n > 1;
  const current = applicants[activeIdx] || emptyApplicant();
  const isLastApplicant = activeIdx === n - 1;
  const hasAddOns = (order.add_ons || []).length > 0;
  // Step 5 (add-ons) is only available on the LAST applicant when add-ons exist.
  const visibleSteps = isLastApplicant && hasAddOns ? [...STEPS, ADDON_STEP] : STEPS;
  const stepIdx = visibleSteps.findIndex((s) => s.key === stepKey);
  const isLastStep = stepIdx === visibleSteps.length - 1;
  const onAddOnsStep = stepKey === "addons";
  const cameFromReview = new URLSearchParams(window.location.search).get("from") === "review";

  const addOnsCompleteNow = !hasAddOns || addOnsAreComplete(order.add_ons || [], addOnDetails);

  const saveAll = async (next) => {
    setSaving(true);
    setSaveError(false);
    try {
      await api.patch(`/orders/${orderId}/applicants`, { applicants: next });
      setSavedAt(new Date());
    } catch (err) {
      setSaveError(true);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const goTo = (i) => navigate(`/apply/applicants/${orderId}/${i}`);
  const onChange = (patched) => {
    setApplicants((prev) => prev.map((a, i) => (i === activeIdx ? patched : a)));
  };

  const goBack = async () => {
    if (onAddOnsStep) {
      // Going back from add-ons step → previous step (additional)
      setStepKey(STEPS[STEPS.length - 1].key);
      return;
    }
    // Autosave before going back
    const next = applicants.map((a, i) => (i === activeIdx ? current : a));
    await saveAll(next);
    if (stepIdx > 0) setStepKey(visibleSteps[stepIdx - 1].key);
    else if (activeIdx > 0) { goTo(activeIdx - 1); setStepKey(STEPS[STEPS.length - 1].key); }
  };

  const saveAddOnDetails = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      await api.patch(`/orders/${orderId}/add-ons`, { add_on_details: addOnDetails });
      setSavedAt(new Date());
    } catch (err) {
      setSaveError(true);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const setAddOnSection = (key, patch) =>
    setAddOnDetails((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const isCustomOrder = order?.order_type === "custom";
    const reviewHrefBase = isCustomOrder ? "/dashboard" : `/apply/review/${orderId}`;
    if (onAddOnsStep) {
      // Final submit on add-ons step → save details + go to review (or dashboard for custom)
      if (!addOnsCompleteNow) return; // submit button is disabled, but guard anyway
      await saveAddOnDetails();
      navigate(reviewHrefBase);
      return;
    }
    const next = applicants.map((a, i) => (i === activeIdx ? current : a));
    await saveAll(next);
    if (stepIdx < visibleSteps.length - 1) {
      // Advance to next step (may be add-ons if visible)
      setStepKey(visibleSteps[stepIdx + 1].key);
      return;
    }
    // We just finished the LAST visible step.
    if (cameFromReview || isLastApplicant) {
      if (hasAddOns) {
        navigate(`/apply/applicants/${orderId}/${activeIdx}?step=addons`);
        setStepKey("addons");
      } else {
        navigate(reviewHrefBase);
      }
    } else {
      goTo(activeIdx + 1);
    }
  };

  const jumpToStep = async (key) => {
    if (key === stepKey) return;
    if (onAddOnsStep) {
      // Leaving add-ons step → autosave the details quietly
      try { await saveAddOnDetails(); } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("ApplyApplicants: add-on details autosave failed (continuing with submission)", err?.response?.status ?? err);
      }
    } else {
      const next = applicants.map((a, i) => (i === activeIdx ? current : a));
      await saveAll(next);
    }
    setStepKey(key);
  };

  // If URL has ?step=addons, jump into that step (handled in top-of-component useEffect above)

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <Link to="/dashboard" className="text-xs text-[#475569] hover:text-[#0f1b2d]">← Back to dashboard</Link>
              <h1 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Applicant details</h1>
              {order.order_type === "custom" ? (
                <p className="mt-1 text-[#475569]" data-testid="applicants-subtitle-custom">
                  Custom File · {(order.selected_documents || []).map((d) => DOC_LABELS[d] || d).join(", ")} · {n} applicant{n > 1 ? "s" : ""}
                </p>
              ) : (
                <p className="mt-1 text-[#475569]">{order.destination_country} · {order.package_name} · {n} applicant{n > 1 ? "s" : ""}</p>
              )}
              {order.order_type === "custom" && (order.selected_documents || []).includes("visa") && (
                <div className="mt-3 rounded-md bg-[#eff6ff] border border-[#bfdbfe] px-3 py-2 text-sm text-[#0f1b2d] inline-flex items-start gap-2" data-testid="custom-visa-info-banner">
                  <span className="text-[#2563eb]">ℹ️</span>
                  <span>We need your passport details to prepare your Visa Application Form. This takes about 10 minutes per applicant.</span>
                </div>
              )}
            </div>
            <Link
              to={hasAddOns ? `/apply/applicants/${orderId}/${n - 1}?step=addons` : (order.order_type === "custom" ? "/dashboard" : `/apply/review/${orderId}`)}
              data-testid="skip-to-review"
              className="text-sm text-[#2563eb] font-semibold hover:underline"
            >
              {hasAddOns ? "Skip to add-ons" : (order.order_type === "custom" ? "Skip to dashboard" : "Skip to review")}
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {showSidebar && (
              <aside className="lg:col-span-3" data-testid="applicants-sidebar">
                <div className="bg-white border border-[#e2e8f0] rounded-lg p-3 sticky top-24">
                  <div className="text-xs uppercase tracking-widest text-[#64748b] px-2 py-2">Applicants</div>
                  <div className="space-y-1">
                    {applicants.map((a, i) => {
                      const complete = applicantIsComplete(a);
                      const isCurrent = i === activeIdx;
                      return (
                        <button
                          key={a._uid || i}
                          onClick={() => goTo(i)}
                          data-testid={`sidebar-applicant-${i}`}
                          className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-2 text-sm transition ${isCurrent ? "bg-[#2563eb] text-white" : "hover:bg-[#f1f5f9] text-[#0f1b2d]"}`}
                        >
                          {complete ? (
                            <CheckCircle2 className={`w-4 h-4 ${isCurrent ? "text-white" : "text-emerald-600"} flex-shrink-0`} />
                          ) : (
                            <Circle className={`w-4 h-4 ${isCurrent ? "text-white/80" : "text-[#cbd5e1]"} flex-shrink-0`} />
                          )}
                            <div className="min-w-0 flex-1">
                            <div className="font-semibold truncate">Applicant {i + 1}</div>
                            <div className={`text-xs truncate ${isCurrent ? "text-white/80" : "text-[#64748b]"}`}>
                              {applicantDisplayName(a) || (complete ? "Completed" : "Pending")}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </aside>
            )}

            <div className={showSidebar ? "lg:col-span-9" : "lg:col-span-12"}>
              <form onSubmit={onSubmit} data-testid="applicant-form">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">Applicant {activeIdx + 1} of {n}</div>
                  <div data-testid="autosave-indicator" className="text-xs inline-flex items-center gap-1.5">
                    {saveStatus === "saving" && (<><Loader2 className="w-3.5 h-3.5 animate-spin text-[#64748b]" /><span className="text-[#64748b]">Saving…</span></>)}
                    {saveStatus === "saved" && (<><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-700">{relativeSavedAgo}</span></>)}
                    {saveStatus === "error" && (<><AlertCircle className="w-3.5 h-3.5 text-red-600" /><span className="text-red-600">Save failed — retrying…</span></>)}
                  </div>
                </div>

                {/* Step tabs — single row with horizontal scroll on mobile */}
                <div
                  ref={tabsRef}
                  className="mb-5 flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1"
                  data-testid="step-tabs"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
                >
                  {visibleSteps.map((s, i) => {
                    const done = s.key === "addons" ? addOnsCompleteNow : stepIsComplete(s.key, current);
                    const active = s.key === stepKey;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => jumpToStep(s.key)}
                        data-testid={`step-tab-${s.key}`}
                        className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-md border text-[11px] sm:text-sm transition flex-shrink-0 ${active ? "bg-[#2563eb] text-white border-[#2563eb]" : "bg-white text-[#0f1b2d] border-[#e2e8f0] hover:border-[#94a3b8]"}`}
                      >
                        <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold flex-shrink-0 ${active ? "bg-white text-[#2563eb]" : done ? "bg-emerald-100 text-emerald-700" : "bg-[#f1f5f9] text-[#64748b]"}`}>{done ? "✓" : i + 1}</span>
                        <span className="whitespace-nowrap">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
                <style>{`[data-testid="step-tabs"]::-webkit-scrollbar { display: none; }`}</style>

                {onAddOnsStep ? (
                  <AddOnDetailsForm
                    addOns={order.add_ons || []}
                    details={addOnDetails}
                    setSection={setAddOnSection}
                  />
                ) : (
                  <ApplicantForm
                    value={current}
                    onChange={onChange}
                    step={stepKey}
                    showRelationship={showRelationship}
                    applicantIndex={activeIdx}
                    destinationCountry={order?.destination_country}
                  />
                )}
                <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    disabled={stepIdx === 0 && activeIdx === 0}
                    onClick={goBack}
                    data-testid="applicant-prev"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f1b2d] disabled:opacity-40"
                  >
                    <ArrowLeft className="w-4 h-4" /> Go Back
                  </button>
                  <button
                    type="submit"
                    disabled={saving || (onAddOnsStep && !addOnsCompleteNow)}
                    data-testid="applicant-save-continue"
                    className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-md"
                  >
                    {saving ? "Saving…" :
                      onAddOnsStep ? (order.order_type === "custom" ? "Save & Finish" : "Save & Review Order") :
                      (isLastStep && isLastApplicant ? (hasAddOns ? "Save & Continue to Add-ons" : (order.order_type === "custom" ? "Save & Finish" : "Save & Review Order")) :
                      isLastStep ? "Save & Continue to next applicant" : "Save and Continue")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
